"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, CheckCircle2, Code, ExternalLink, MessageSquare, Server, ShieldCheck, Trophy, Zap } from "lucide-react";
import { Badge } from "@/lib/components/badge";
import { useAppConfig } from "@/lib/config";
import { cn } from "@/lib/utils";
import { getProviderIcon } from "@llmgateway/shared/components";
interface ProviderBenchmark {
  providerId: string;
  providerName: string;
  logsCount: number;
  errorsCount: number;
  cachedCount: number;
  avgTimeToFirstToken: number | null;
  tokensPerSecond: number | null;
  errorRate: number;
  uptime: number | null;
  windowHours: number;
}
interface ArenaScore {
  rank: number;
  score: number;
  matchedName: string;
}
interface ArenaBenchmark {
  text: ArenaScore | null;
  code: ArenaScore | null;
  source: string;
  fetchedAt: string;
}
interface BenchmarkData {
  modelId: string;
  providers: ProviderBenchmark[];
  arena: ArenaBenchmark;
}

// Below this many requests in the window, throughput/uptime/error rates are too
// noisy to be statistically meaningful, so we hide them rather than mislead.
const MIN_SIGNIFICANT_REQUESTS = 1000;
export function ModelBenchmarks(t0) {
  const $ = _c(24);
  const {
    modelId
  } = t0;
  const config = useAppConfig();
  let t1;
  if ($[0] !== modelId) {
    t1 = ["model-benchmarks", modelId];
    $[0] = modelId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== config || $[3] !== modelId) {
    t2 = async () => {
      const response = await fetch(`${config.apiUrl}/internal/models/${encodeURIComponent(modelId)}/benchmarks`);
      if (!response.ok) {
        throw new Error("Failed to fetch benchmarks");
      }
      return await response.json();
    };
    $[2] = config;
    $[3] = modelId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      staleTime: 300000
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data,
    isLoading
  } = useQuery(t3);
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[8] !== data?.arena || $[9] !== data?.providers || $[10] !== isLoading) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const providers = data?.providers ?? [];
      const arena = data?.arena;
      const hasProviderData = providers.some(_temp);
      const hasArenaData = arena?.text !== null || arena?.code !== null;
      if (isLoading) {
        let t8;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <div className="h-6 w-48 animate-pulse rounded bg-muted mb-4" />;
          $[15] = t8;
        } else {
          t8 = $[15];
        }
        let t9;
        if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <div className="rounded-lg border border-border p-6">{t8}<div className="space-y-3">{[1, 2, 3].map(_temp2)}</div></div>;
          $[16] = t9;
        } else {
          t9 = $[16];
        }
        t7 = t9;
        break bb0;
      }
      if (!hasProviderData && !hasArenaData) {
        t7 = null;
        break bb0;
      }
      const sorted = [...providers].filter(_temp3).sort(_temp4);
      const stabilityCandidates = sorted.filter(_temp5);
      const mostStableProviderId = stabilityCandidates.length > 1 ? stabilityCandidates[0].providerId : null;
      t4 = "space-y-8";
      if ($[17] !== arena || $[18] !== hasArenaData) {
        t5 = hasArenaData && <div><div className="flex items-center gap-2 mb-4"><Trophy className="h-5 w-5 text-muted-foreground" /><h2 className="text-xl md:text-2xl font-semibold">Quality Benchmarks</h2></div><p className="text-sm text-muted-foreground mb-4">Crowdsourced quality ratings from{" "}<a href={arena?.source ?? "https://arena.ai/leaderboard"} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground inline-flex items-center gap-1">Chatbot Arena<ExternalLink className="h-3 w-3" /></a>. Higher ELO score = better quality. Updated{" "}{arena?.fetchedAt ?? "recently"}.</p><div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{arena?.text && <div className="rounded-lg border border-border p-4"><div className="flex items-center gap-2 mb-3"><MessageSquare className="h-4 w-4 text-blue-500" /><span className="text-sm font-medium">Overall (Text)</span></div><div className="flex items-baseline gap-3"><span className="text-3xl font-bold tabular-nums">{arena.text.score}</span><span className="text-sm text-muted-foreground">ELO</span></div><div className="mt-2 flex items-center gap-2"><Badge variant="outline" className={cn("text-xs", arena.text.rank <= 10 ? "text-amber-600 border-amber-500/50" : arena.text.rank <= 30 ? "text-blue-600 border-blue-500/50" : "")}>#{arena.text.rank}</Badge><span className="text-xs text-muted-foreground truncate">{arena.text.matchedName}</span></div></div>}{arena?.code && <div className="rounded-lg border border-border p-4"><div className="flex items-center gap-2 mb-3"><Code className="h-4 w-4 text-purple-500" /><span className="text-sm font-medium">Coding</span></div><div className="flex items-baseline gap-3"><span className="text-3xl font-bold tabular-nums">{arena.code.score}</span><span className="text-sm text-muted-foreground">ELO</span></div><div className="mt-2 flex items-center gap-2"><Badge variant="outline" className={cn("text-xs", arena.code.rank <= 10 ? "text-amber-600 border-amber-500/50" : arena.code.rank <= 30 ? "text-blue-600 border-blue-500/50" : "")}>#{arena.code.rank}</Badge><span className="text-xs text-muted-foreground truncate">{arena.code.matchedName}</span></div></div>}</div></div>;
        $[17] = arena;
        $[18] = hasArenaData;
        $[19] = t5;
      } else {
        t5 = $[19];
      }
      t6 = sorted.length > 0 && <div><div className="flex items-center gap-2 mb-4"><Activity className="h-5 w-5 text-muted-foreground" /><h2 className="text-xl md:text-2xl font-semibold">Provider Performance</h2></div><p className="text-sm text-muted-foreground mb-4">Real performance data from LLM Gateway over the last{" "}{sorted[0]?.windowHours ?? 24} hours. Higher uptime and throughput are better. Stats are hidden for providers with under{" "}{MIN_SIGNIFICANT_REQUESTS.toLocaleString()} requests, where the sample is too small to be reliable.</p><div className="grid gap-3">{sorted.map(provider => {
            const ProviderIcon = getProviderIcon(provider.providerId);
            const isMostStable = provider.providerId === mostStableProviderId;
            const isSignificant = provider.logsCount >= MIN_SIGNIFICANT_REQUESTS;
            const insufficientDataTitle = `Not enough traffic for reliable stats (under ${MIN_SIGNIFICANT_REQUESTS.toLocaleString()} requests)`;
            return <div key={provider.providerId} className={cn("rounded-lg border p-4 transition-colors", isMostStable ? "border-green-500/50 bg-green-50/50 dark:bg-green-950/20" : "border-border")}><div className="flex items-center justify-between gap-4 flex-wrap"><div className="flex items-center gap-3 min-w-0">{ProviderIcon ? <ProviderIcon className="h-5 w-5 shrink-0" /> : <Server className="h-5 w-5 shrink-0 text-muted-foreground" />}<div className="min-w-0"><div className="flex items-center gap-2"><span className="font-medium truncate">{provider.providerName}</span>{isMostStable && <Badge variant="outline" className="text-green-600 border-green-500/50 text-xs gap-1"><ShieldCheck className="h-3 w-3" />Most stable</Badge>}</div><span className="text-xs text-muted-foreground">{provider.logsCount.toLocaleString()} requests</span></div></div><div className="flex items-center gap-6 text-sm"><div className="text-center"><div className="flex items-center gap-1 text-muted-foreground mb-0.5"><Zap className="h-3 w-3" /><span className="text-xs">Throughput</span></div><span className={cn("font-mono font-medium", !isSignificant || provider.tokensPerSecond === null ? "text-muted-foreground" : "")} title={isSignificant ? "Output tokens per second across all requests in the window" : insufficientDataTitle}>{isSignificant && provider.tokensPerSecond !== null ? `${provider.tokensPerSecond.toLocaleString()} tok/s` : "\u2014"}</span></div><div className="text-center"><div className="flex items-center gap-1 text-muted-foreground mb-0.5"><CheckCircle2 className="h-3 w-3" /><span className="text-xs">Uptime</span></div><span className={cn("font-mono font-medium", !isSignificant || provider.uptime === null ? "text-muted-foreground" : provider.uptime >= 99 ? "text-green-600" : provider.uptime >= 95 ? "text-amber-500" : "text-red-500")} title={isSignificant ? `Uptime over last ${provider.windowHours}h` : insufficientDataTitle}>{isSignificant && provider.uptime !== null ? `${provider.uptime.toFixed(2)}%` : "\u2014"}</span></div><div className="text-center hidden sm:block"><div className="flex items-center gap-1 text-muted-foreground mb-0.5"><AlertTriangle className="h-3 w-3" /><span className="text-xs">Errors</span></div><span className={cn("font-mono font-medium", !isSignificant ? "text-muted-foreground" : provider.errorRate > 5 ? "text-red-500" : provider.errorRate > 1 ? "text-amber-500" : "text-green-600")} title={isSignificant ? undefined : insufficientDataTitle}>{isSignificant ? `${provider.errorRate}%` : "\u2014"}</span></div></div></div></div>;
          })}</div></div>;
    }
    $[8] = data?.arena;
    $[9] = data?.providers;
    $[10] = isLoading;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t8 = <div className={t4}>{t5}{t6}</div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp5(p_1) {
  return p_1.uptime !== null && p_1.logsCount >= MIN_SIGNIFICANT_REQUESTS;
}
function _temp4(a, b) {
  if (a.uptime === null && b.uptime === null) {
    return b.logsCount - a.logsCount;
  }
  if (a.uptime === null) {
    return 1;
  }
  if (b.uptime === null) {
    return -1;
  }
  if (b.uptime !== a.uptime) {
    return b.uptime - a.uptime;
  }
  return b.logsCount - a.logsCount;
}
function _temp3(p_0) {
  return p_0.logsCount > 0;
}
function _temp2(i) {
  return <div key={i} className="h-16 animate-pulse rounded bg-muted" />;
}
function _temp(p) {
  return p.logsCount > 0;
}